import React, { useState, useRef, useEffect } from "react";
import EmojiPicker from "emoji-picker-react";
import "./Chatbot.css";

const HospitalityChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [providers, setProviders] = useState([]);
  const messagesEndRef = useRef(null);

  // ------------------ Fetch providers from backend ------------------
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/providers`)
      .then((res) => res.json())
      .then((data) => setProviders(data))
      .catch((err) => console.error("Failed to fetch providers:", err));
  }, []);

  // ------------------ Scroll to bottom ------------------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const addMessage = (text, sender = "bot") => {
    setMessages((prev) => [...prev, { text, sender }]);
  };

  // ------------------ Initial greeting ------------------
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addMessage("Welcome! 🍽️ How can I help you today?");
      addMessage(
        "You can search for Cafes ☕, Restaurants 🍽️, Hotels 🏨, Bars 🍹, and Clubs 🎶💃, or a specific place."
      );
    }
  }, [isOpen]);

  // ------------------ Send message handler ------------------
  const handleSend = () => {
    const msg = input.trim();
    if (!msg) return;

    addMessage(msg, "user");
    setInput("");

    const lowerMsg = msg.toLowerCase();

    // Emoji detection
    const emojiRegex = /(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/u;
    if (emojiRegex.test(msg)) {
      const emojiReplies = [
        "Love that vibe! 😍",
        "Nice choice! 🍹",
        "That looks delicious 😋",
        "Great mood! 🥳",
        "Hospitality vibes! ✨",
      ];
      addMessage(
        emojiReplies[Math.floor(Math.random() * emojiReplies.length)]
      );
      return;
    }

    // Greetings
    if (lowerMsg.includes("hello") || lowerMsg.includes("hi")) {
      addMessage("Hello! 😊 Looking for a place to eat, stay, or enjoy?");
      return;
    }

    // ------------------ Match providers dynamically ------------------
    if (!providers.length) {
      addMessage("No providers loaded yet. Please wait...");
      return;
    }

    const matchedProviders = providers.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const category = (p.category || "").toLowerCase();

      const branchText = (p.branches || [])
        .map((b) =>
          [
            b.branch_name || "",
            b.address || "",
            b.services || "",
          ].join(" ")
        )
        .join(" ")
        .toLowerCase();

      return (
        name.includes(lowerMsg) ||
        category.includes(lowerMsg) ||
        branchText.includes(lowerMsg)
      );
    });

    if (matchedProviders.length === 0) {
      addMessage("Sorry, I couldn't find any matching places 😕");
      return;
    }

    const response = matchedProviders
      .map((p) => {
        const branchesText =
          p.branches && p.branches.length > 0
            ? p.branches
              .map(
                (b) => `📍 Branch: ${b.branch_name || "Unnamed branch"}
📍 Address: ${b.address || "Not available"}
🍽️ Services: ${b.services || "Not listed"}`
              )
              .join("\n")
            : "No branch information available";

        return `📍 ${p.name || "Unnamed"} (${p.category || "No category"})
⭐ Rating: ${p.rating ?? "N/A"}${p.review_count ? ` (${p.review_count} reviews)` : ""
          }
${branchesText}`;
      })
      .join("\n\n");

    addMessage(response);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSend();
  };

  const handleEmojiClick = (emojiData) => {
    setInput((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  return (
    <>
      {/* Floating button */}
      <div className="chatbot-button" onClick={handleToggle}>
        <span className="chatbot-icon" style={{ backgroundColor: "#f97316" }}>
          🍽️
        </span>
        <span className="chatbot-label">
          {isOpen ? "Close Chat" : "Ask iSpotera"}
        </span>
      </div>

      {/* Chat window */}
      {isOpen && (
        <div className="chatbot-window">
          <div
            className="chatbot-header"
            style={{ backgroundColor: "#f97316" }}
          >
            <span>iSpotera Assistant</span>
            <button className="chatbot-close" onClick={handleToggle}>
              ✖
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`chatbot-message ${m.sender === "user" ? "user" : "bot"
                  }`}
                style={
                  m.sender === "user"
                    ? { backgroundColor: "#f97316" }
                    : {}
                }
              >
                {m.text.split("\n").map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input-container">
            <input
              type="text"
              placeholder="Ask about food, hotels, events..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button onClick={() => setShowEmojiPicker((p) => !p)}>😊</button>
            <button onClick={handleSend}>Send</button>
          </div>

          {showEmojiPicker && (
            <div className="emoji-picker">
              <EmojiPicker onEmojiClick={handleEmojiClick} />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default HospitalityChatbot;
